import { toSvg } from "html-to-image";

/**
 * Что было на экране в момент жалобы.
 *
 * Текст — то, что читает тот, кто чинит: ищется, цитируется, весит
 * килобайты. Снимок — единственное доказательство для раскладки: «кнопка не
 * там» текстом не показать. Настоящий снимок экрана браузер сам сделать не
 * может — только через диалог выбора окна, а это убило бы одно нажатие.
 * Поэтому снимок собирается из DOM: для нашего интерфейса точно.
 *
 * Берётся рабочая область (`main`), без панели навигации: она одинаковая на
 * всех экранах и только занимает место.
 *
 * Снимается **видимая часть**, а не страница целиком. На прогоне 21.09 три
 * жалобы со справочника приехали одинаковыми полотнами 1364×6685: место, на
 * которое жалуются, приходилось искать глазами по шести экранам. Человек
 * жалуется на то, что видит, — снимок обязан показывать то же самое, и
 * выделенное место в нём обведено.
 *
 * Плавающие панели (`data-no-capture`) в снимок не идут: при съёмке всей
 * страницы `position: fixed` попадает на своё экранное место, то есть
 * посередине полотна, — и закрывает как раз то, на что жалуются.
 */
export interface ScreenCapture {
  text: string;
  /** JPEG как data-URL; пусто, если снять не удалось или он слишком велик */
  image?: string;
  /** почему снимка нет — для честной строки в отчёте */
  imageError?: string;
}

const TEXT_LIMIT = 12_000;
const IMAGE_LIMIT = 1_500_000;

/**
 * Сколько ждём снимок.
 *
 * Жалоба — одно нажатие, и оно не может висеть. Не успел — уходит текст, а
 * причина остаётся в консоли. Это лучше, чем крутящаяся кнопка и жалоба,
 * которую так и не отправили. На «Материалах» сборка занимает около двух
 * секунд, «Расчёт» с сотней строк тяжелее — отсюда запас.
 */
const IMAGE_TIMEOUT_MS = 6_000;

/**
 * Какие CSS-свойства уносим в снимок.
 *
 * По умолчанию библиотека копирует в каждый узел все триста с лишним
 * вычисленных свойств: на «Расчёте» с тысячей узлов это десять секунд и
 * шестнадцать мегабайт SVG — снимок не успевал в таймаут. Со списком ниже —
 * полторы секунды и та же картинка: список собран по нашему интерфейсу
 * попиксельным сравнением с полным снимком (разошлись только края иконок).
 * Если в интерфейсе появится новое свойство, которое влияет на вид, — сюда.
 */
const STYLE_PROPS = [
  // коробка и положение
  "display",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "inset",
  "float",
  "clear",
  "z-index",
  "box-sizing",
  "width",
  "height",
  "min-width",
  "min-height",
  "max-width",
  "max-height",
  "aspect-ratio",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-top-style",
  "border-right-style",
  "border-bottom-style",
  "border-left-style",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-left-radius",
  "border-bottom-right-radius",
  "outline-width",
  "outline-style",
  "outline-color",
  "outline-offset",
  "overflow-x",
  "overflow-y",
  // flex и grid
  "flex-direction",
  "flex-wrap",
  "flex-grow",
  "flex-shrink",
  "flex-basis",
  "justify-content",
  "justify-self",
  "align-items",
  "align-self",
  "align-content",
  "row-gap",
  "column-gap",
  "grid-template-columns",
  "grid-template-rows",
  "grid-auto-flow",
  "grid-auto-columns",
  "grid-auto-rows",
  "grid-column-start",
  "grid-column-end",
  "grid-row-start",
  "grid-row-end",
  "order",
  // текст
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "font-stretch",
  "font-variant",
  "font-variant-numeric",
  "font-feature-settings",
  "font-kerning",
  "line-height",
  "letter-spacing",
  "word-spacing",
  "text-align",
  "text-decoration-line",
  "text-decoration-color",
  "text-decoration-style",
  "text-decoration-thickness",
  "text-underline-offset",
  "text-transform",
  "text-overflow",
  "text-indent",
  "text-shadow",
  "text-rendering",
  "white-space",
  "word-break",
  "overflow-wrap",
  "hyphens",
  "vertical-align",
  "direction",
  "unicode-bidi",
  "writing-mode",
  "tab-size",
  "-webkit-line-clamp",
  "-webkit-box-orient",
  // цвет и эффекты
  "color",
  "background-color",
  "background-image",
  "background-size",
  "background-position",
  "background-repeat",
  "background-clip",
  "background-origin",
  "opacity",
  "visibility",
  "box-shadow",
  "transform",
  "transform-origin",
  "translate",
  "scale",
  "rotate",
  "clip-path",
  "filter",
  "backdrop-filter",
  "mix-blend-mode",
  "isolation",
  "object-fit",
  "object-position",
  // svg
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  // списки, таблицы, формы
  "list-style-type",
  "list-style-position",
  "table-layout",
  "border-collapse",
  "border-spacing",
  "caption-side",
  "empty-cells",
  "appearance",
  "content",
  "quotes",
];

/**
 * Растр делаем сами, а не через `toJpeg` библиотеки: та между загрузкой
 * картинки и отрисовкой ждёт `requestAnimationFrame`, который в скрытой
 * вкладке не приходит никогда — снимок висел до таймаута и в отчёт не
 * попадал. SVG → `Image.decode()` → canvas обходятся без кадра.
 */
async function rasterise(root: HTMLElement, highlight?: DOMRect): Promise<string | undefined> {
  const svg = await toSvg(root, {
    backgroundColor: "#ffffff",
    cacheBust: false,
    // Плавающие панели: в снимке они оказываются не там, где на экране.
    filter: (node) => !(node instanceof HTMLElement && node.dataset.noCapture !== undefined),
    // Шрифты не втягиваем: они с Google Fonts, внешний CSS из страницы не
    // читается и тянет сетевые запросы без конца. В снимке будет системный
    // шрифт — раскладку это не меняет.
    skipFonts: true,
    includeStyleProperties: STYLE_PROPS,
  });
  const img = new Image();
  img.src = svg;
  await img.decode();

  // Что из `main` сейчас на экране — в координатах самого `main`, потому что
  // отрисованная картинка начинается с его левого верхнего угла.
  const box = root.getBoundingClientRect();
  const sx = Math.max(0, -box.left);
  const sy = Math.max(0, -box.top);
  const sw = Math.max(1, Math.min(img.width - sx, window.innerWidth - Math.max(0, box.left)));
  const sh = Math.max(1, Math.min(img.height - sy, window.innerHeight - Math.max(0, box.top)));

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas недоступен");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

  if (highlight) frame(ctx, highlight, box, sx, sy);

  for (const quality of [0.8, 0.6, 0.45]) {
    const image = canvas.toDataURL("image/jpeg", quality);
    if (image.length <= IMAGE_LIMIT) return image;
  }
  return undefined;
}

/**
 * Рамка вокруг того, на что жалуются.
 *
 * Без неё снимок отвечает на вопрос «как выглядел экран», а нужен ответ на
 * «где именно не так»: выделенный текст в задачу не уходит (в нём бывают
 * ставки), и на снимке это единственный указатель.
 */
function frame(
  ctx: CanvasRenderingContext2D,
  target: DOMRect,
  box: DOMRect,
  sx: number,
  sy: number,
): void {
  const x = target.left - box.left - sx;
  const y = target.top - box.top - sy;
  const pad = 4;

  ctx.save();
  ctx.strokeStyle = "#e11d48";
  ctx.lineWidth = 2;
  ctx.strokeRect(x - pad, y - pad, target.width + pad * 2, target.height + pad * 2);
  ctx.restore();
}

export async function captureScreen(highlight?: DOMRect): Promise<ScreenCapture> {
  const root = document.querySelector<HTMLElement>("main") ?? document.body;
  const text = root.innerText
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, TEXT_LIMIT);

  const render = async (): Promise<ScreenCapture> => {
    const image = await rasterise(root, highlight);
    return image ? { text, image } : { text, imageError: "снимок больше полутора мегабайт" };
  };

  const timeout = new Promise<ScreenCapture>((resolve) =>
    setTimeout(
      () => resolve({ text, imageError: "снимок не собрался за шесть секунд" }),
      IMAGE_TIMEOUT_MS,
    ),
  );

  try {
    return await Promise.race([render(), timeout]);
  } catch (err) {
    console.warn("[capture] снимок не собрался", err);
    return { text, imageError: "страницу не удалось отрисовать" };
  }
}
